import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import settingsService from '../services/settings.service';

// Cache for Sentry configuration
let sentryConfigCache = null;
let sentryConfigPromise = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let cacheTimestamp = null;

// Browser extensions inject <script> tags into the page. That code runs in OUR
// JS context, so its uncaught errors reach Sentry's global window.onerror handler
// and are indistinguishable from ours. Filtering has to be explicit.
//
// Filter by the script's origin rather than by message text: a message list is
// whack-a-mole — every extension, on every user's browser, brings its own strings.
export const EXTENSION_URL_PATTERNS = [
  'chrome-extension://',
  'safari-web-extension://',
  'safari-extension://',
  'moz-extension://',
  'ms-browser-extension://',
];

// Extension errors frequently arrive with no usable stack (anonymous injected
// code), so the URL filter above cannot see them. These are signatures of the
// extension messaging APIs, which our code never calls.
export const EXTENSION_ERROR_SIGNATURES = [
  'runtime.sendmessage',
  'chrome.runtime',
  'browser.runtime',
  'extension context invalidated',
  'tab not found',
  'receiving end does not exist',
  // Pre-existing entries, kept: these arrive stack-less too.
  'i18n.text called before ready',
  'sidepaneltoggle',
  'go/i18n-help',
];

const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  const h = window.location?.hostname || '';
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h.endsWith('.local');
};

/**
 * `sentry_environment` comes from the shared settings table, so a developer
 * running against it reports as "production" and pollutes production error
 * stats with local noise. The page's own hostname is the ground truth.
 */
export const resolveEnvironment = (configured) => {
  if (isLocalHost()) return 'development';
  return configured || import.meta.env.MODE || 'development';
};

export const isExtensionUrl = (url) =>
  typeof url === 'string' && EXTENSION_URL_PATTERNS.some((p) => url.includes(p));

/**
 * Get Sentry configuration from settings service
 * @returns {Promise<Object>} Sentry configuration
 */
async function getSentryConfig() {
  // Check cache validity
  if (sentryConfigCache && cacheTimestamp && 
      (Date.now() - cacheTimestamp < CACHE_TTL)) {
    return sentryConfigCache;
  }
  
  // If already loading, return the promise
  if (sentryConfigPromise) {
    return sentryConfigPromise;
  }
  
  // Load configuration from settings
  sentryConfigPromise = loadSentryConfigFromSettings();
  sentryConfigCache = await sentryConfigPromise;
  cacheTimestamp = Date.now();
  sentryConfigPromise = null;
  
  return sentryConfigCache;
}

/**
 * Load Sentry configuration from settings service
 * @private
 */
async function loadSentryConfigFromSettings() {
  try {
    // Define all Sentry settings we need
    const settingNames = [
      'sentry_enabled',
      'sentry_dsn', 
      'sentry_environment',
      'sentry_debug',
      'sentry_traces_sample_rate',
      'sentry_replays_session_sample_rate', 
      'sentry_replays_on_error_sample_rate',
      'sentry_release',
      'sentry_mask_all_text',
      'sentry_block_all_media'
    ];
    
    // Default values based on environment
    const defaults = {
      sentry_enabled: import.meta.env.MODE === 'production',
      sentry_dsn: '',
      sentry_environment: import.meta.env.MODE || 'development',
      sentry_debug: import.meta.env.MODE === 'development',
      sentry_traces_sample_rate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
      sentry_replays_session_sample_rate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
      sentry_replays_on_error_sample_rate: 1.0,
      sentry_release: 'unknown',
      sentry_mask_all_text: false,
      sentry_block_all_media: false
    };
    
    // NUEVA ARQUITECTURA: Check if we have authentication available from UserSessionRegistry
    let hasAuth = false;
    try {
      const { UserSessionRegistry } = await import('../stores/user-session.registry');
      const userData = await UserSessionRegistry.getCurrentUser();
      hasAuth = !!userData?.email;
    } catch (e) {
      console.warn('[Sentry] Could not check auth from UserSessionRegistry:', e);
      // NO FALLBACK to localStorage - assume no auth
      hasAuth = false;
    }
    
    let settings = defaults;
    
    // Only try to load from API if we have authentication
    if (hasAuth) {
      try {
        console.log('📊 Loading Sentry configuration from API (authenticated)...');
        // Get all settings from backend
        settings = await settingsService.getMultiple(settingNames, defaults);
        
        console.log('📊 Sentry configuration loaded from settings:', {
          enabled: settings.sentry_enabled,
          environment: settings.sentry_environment,
          hasDSN: !!settings.sentry_dsn,
          debug: settings.sentry_debug
        });
      } catch (apiError) {
        console.warn('⚠️ Failed to load Sentry settings from API, using defaults:', apiError.message);
        settings = defaults;
      }
    } else {
      console.log('📊 No authentication available, using default Sentry configuration');
    }
    
    return {
      enabled: settings.sentry_enabled,
      dsn: settings.sentry_dsn,
      environment: resolveEnvironment(settings.sentry_environment),
      debug: settings.sentry_debug,
      tracesSampleRate: settings.sentry_traces_sample_rate,
      replaysSessionSampleRate: settings.sentry_replays_session_sample_rate,
      replaysOnErrorSampleRate: settings.sentry_replays_on_error_sample_rate,
      release: settings.sentry_release,
      maskAllText: settings.sentry_mask_all_text,
      blockAllMedia: settings.sentry_block_all_media,
      apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000'
    };
  } catch (error) {
    console.error('❌ Failed to load Sentry settings, using minimal defaults:', error);
    
    // Return minimal default configuration to prevent app crash
    return {
      enabled: false, // Disable Sentry if we can't load settings
      dsn: '',
      environment: import.meta.env.MODE || 'development',
      debug: import.meta.env.MODE === 'development',
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      release: 'unknown',
      maskAllText: false,
      blockAllMedia: false,
      apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000'
    };
  }
}

/**
 * Get tracing origins for performance monitoring
 * @param {string} apiUrl - API URL
 * @returns {Array} Array of tracing origins
 */
function getTracingOrigins(apiUrl) {
  return [
    'localhost',
    apiUrl,
    /^https:\/\/[^/]*\.bt4sap\.com/,
    /^https:\/\/[^/]*\.googleapis\.com/
  ];
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export const initSentry = async () => {
  try {
    const config = await getSentryConfig();
    
    if (!config.enabled || !config.dsn) {
      console.log('🔧 Sentry disabled or DSN not configured');
      return;
    }

    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      debug: config.debug,

      // Drop anything whose stack points at an injected extension script,
      // before it ever reaches beforeSend.
      denyUrls: EXTENSION_URL_PATTERNS,

      // Performance monitoring
      integrations: [
        new BrowserTracing({
          // Set tracing origins to track API calls
          tracePropagationTargets: getTracingOrigins(config.apiUrl),
        }),
        new Sentry.Replay({
          // Capture replays on errors and a percentage of sessions
          maskAllText: config.maskAllText,
          blockAllMedia: config.blockAllMedia,
        }),
      ],
      
      // Performance monitoring sample rates
      tracesSampleRate: config.tracesSampleRate,
      
      // Session replay sample rates
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
      
      // Release tracking
      release: config.release,
      
      // Filter out common non-critical errors
      beforeSend(event, hint) {
        const error = hint.originalException;
        
        // Filter out network errors that are not actionable
        if (error && error.message) {
          const message = error.message.toLowerCase();
          
          // Skip common network issues
          if (
            message.includes('network error') ||
            message.includes('failed to fetch') ||
            message.includes('load failed') ||
            message.includes('script error') ||
            message.includes('non-error promise rejection')
          ) {
            return null;
          }
          
          // Skip authentication errors in development (they're expected)
          if (
            config.environment === 'development' &&
            (message.includes('401') || message.includes('403'))
          ) {
            return null;
          }
          
          // Browser-extension errors that arrive with no usable stack, so
          // denyUrls and the frame check below cannot see them.
          if (EXTENSION_ERROR_SIGNATURES.some((sig) => message.includes(sig))) {
            return null;
          }
        }

        // Filter by error source — extensions inject code into the page.
        // Covers Safari and Firefox too, not just Chrome: a Safari extension
        // reports as safari-web-extension://, which the previous Chrome-only
        // check never matched.
        if (event.exception && event.exception.values) {
          for (const exception of event.exception.values) {
            if (exception.stacktrace && exception.stacktrace.frames) {
              for (const frame of exception.stacktrace.frames) {
                if (
                  frame.filename &&
                  (frame.filename.includes('VM') ||
                   frame.filename.includes('everywhere.js') ||
                   isExtensionUrl(frame.filename))
                ) {
                  return null;
                }
              }
            }
          }
        }
        
        return event;
      },
      
      // Add user context automatically
      initialScope: {
        tags: {
          component: 'frontend',
          framework: 'react'
        }
      }
    });

    console.log('✅ Sentry initialized successfully with dynamic settings');
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
};

/**
 * Clear Sentry configuration cache
 */
export const clearSentryCache = () => {
  sentryConfigCache = null;
  sentryConfigPromise = null;
  cacheTimestamp = null;
  console.log('🔄 Sentry configuration cache cleared');
};

/**
 * Reload Sentry configuration after authentication
 * This should be called after user login to update configuration from API
 */
export const reloadSentryConfigAfterAuth = async () => {
  try {
    console.log('🔄 Reloading Sentry configuration after authentication...');
    
    // Clear cache to force reload
    clearSentryCache();
    
    // Get new configuration
    const config = await getSentryConfig();
    
    // If Sentry is now enabled and wasn't before, or configuration changed significantly
    if (config.enabled && config.dsn) {
      console.log('✅ Sentry configuration updated after authentication');
      
      // Update Sentry user context if possible (NUEVA ARQUITECTURA - NO localStorage)
      try {
        const { UserSessionRegistry } = await import('../stores/user-session.registry');
        const userData = await UserSessionRegistry.getCurrentUser();
        if (userData?.email) {
          // Get organization name from userInfo if available
          let organizationName = null;
          if (userData.userInfo?.user?.members?.[0]?.organization?.name) {
            organizationName = userData.userInfo.user.members[0].organization.name;
          } else if (userData.userInfo?.user?.members?.[0]?.organization?.organization_name) {
            organizationName = userData.userInfo.user.members[0].organization.organization_name;
          }
          
          await setSentryUser({
            email: userData.email,
            id: userData.userId || userData.userInfo?.user?.id || userData.id,
            organization: organizationName || userData.organizationId?.toString() || null
          });
        }
      } catch (e) {
        console.warn('[Sentry] Could not get user data from UserSessionRegistry:', e);
        // NO FALLBACK to localStorage - skip Sentry user context
      }
      
      // Legacy code removed - was reading from localStorage (OBSOLETE)
      if (false) {
        try {
          const authDataObj = JSON.parse(localStorage.getItem('authData') || '{}');
          if (authDataObj.email) {
            await setSentryUser({
              email: authDataObj.email,
              id: authDataObj.user_id || authDataObj.bt_id,
              organization: authDataObj.organization_name
            });
          }
        } catch (parseError) {
          console.warn('Could not parse auth data for Sentry user context:', parseError);
        }
      }
    }
    
    return config;
  } catch (error) {
    console.error('❌ Failed to reload Sentry configuration after auth:', error);
    return null;
  }
};

/**
 * Check if Sentry is enabled
 * @returns {Promise<boolean>} Whether Sentry is enabled
 */
export const isSentryEnabled = async () => {
  try {
    const config = await getSentryConfig();
    return config.enabled && !!config.dsn;
  } catch (error) {
    console.warn('Failed to check Sentry status:', error);
    return false;
  }
};

/**
 * Set user context for Sentry
 * @param {Object} user - User information
 */
export const setSentryUser = async (user) => {
  const enabled = await isSentryEnabled();
  if (!enabled) return;
  
  Sentry.setUser({
    id: user.uid || user.id,
    email: user.email,
    username: user.displayName || user.name,
    organization: user.organization?.name,
    role: user.role
  });
};

/**
 * Clear user context from Sentry
 */
export const clearSentryUser = async () => {
  const enabled = await isSentryEnabled();
  if (!enabled) return;
  
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for user actions
 * @param {string} message - Breadcrumb message
 * @param {string} category - Breadcrumb category
 * @param {Object} data - Additional data
 */
export const addSentryBreadcrumb = async (message, category = 'user', data = {}) => {
  const enabled = await isSentryEnabled();
  if (!enabled) return;
  
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data
  });
};

/**
 * Capture exception with context
 * @param {Error} error - Error to capture
 * @param {Object} context - Additional context
 */
export const captureException = async (error, context = {}) => {
  const enabled = await isSentryEnabled();
  if (!enabled) {
    console.error('Error (Sentry disabled):', error, context);
    return;
  }
  
  Sentry.withScope((scope) => {
    // Add context as tags and extra data
    if (context.tags) {
      Object.keys(context.tags).forEach(key => {
        scope.setTag(key, context.tags[key]);
      });
    }
    
    if (context.extra) {
      Object.keys(context.extra).forEach(key => {
        scope.setExtra(key, context.extra[key]);
      });
    }
    
    // Set level if provided
    if (context.level) {
      scope.setLevel(context.level);
    }
    
    // Add fingerprint for grouping similar errors
    if (context.fingerprint) {
      scope.setFingerprint(context.fingerprint);
    }
    
    Sentry.captureException(error);
  });
};

/**
 * Capture message with context
 * @param {string} message - Message to capture
 * @param {string} level - Message level (info, warning, error)
 * @param {Object} context - Additional context
 */
export const captureMessage = async (message, level = 'info', context = {}) => {
  const enabled = await isSentryEnabled();
  if (!enabled) {
    console.log(`Message (Sentry disabled) [${level}]:`, message, context);
    return;
  }
  
  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.keys(context.tags).forEach(key => {
        scope.setTag(key, context.tags[key]);
      });
    }
    
    if (context.extra) {
      Object.keys(context.extra).forEach(key => {
        scope.setExtra(key, context.extra[key]);
      });
    }
    
    scope.setLevel(level);
    Sentry.captureMessage(message);
  });
};

/**
 * Start a transaction for performance monitoring
 * @param {string} name - Transaction name
 * @param {string} op - Operation type
 * @returns {Object} Transaction object
 */
export const startTransaction = async (name, op = 'navigation') => {
  const enabled = await isSentryEnabled();
  if (!enabled) {
    return { finish: () => {} }; // Return mock transaction
  }
  
  return Sentry.startTransaction({ name, op });
};

/**
 * Wrap API calls with Sentry monitoring
 * @param {string} apiName - Name of the API call
 * @param {Function} apiCall - Function that makes the API call
 * @param {Object} context - Additional context
 * @returns {Promise} API call result
 */
export const monitorApiCall = async (apiName, apiCall, context = {}) => {
  const transaction = await startTransaction(`API: ${apiName}`, 'http');
  
  try {
    await addSentryBreadcrumb(`API call started: ${apiName}`, 'api', context);
    
    const result = await apiCall();
    
    await addSentryBreadcrumb(`API call succeeded: ${apiName}`, 'api', {
      ...context,
      success: true
    });
    
    transaction.setStatus('ok');
    return result;
  } catch (error) {
    await addSentryBreadcrumb(`API call failed: ${apiName}`, 'api', {
      ...context,
      error: error.message,
      success: false
    });
    
    await captureException(error, {
      tags: {
        api_name: apiName,
        error_type: 'api_error'
      },
      extra: {
        api_context: context,
        endpoint: context.endpoint || apiName
      }
    });
    
    transaction.setStatus('internal_error');
    throw error;
  } finally {
    transaction.finish();
  }
};

/**
 * Monitor user interactions
 * @param {string} action - User action
 * @param {Object} data - Action data
 */
export const trackUserAction = async (action, data = {}) => {
  await addSentryBreadcrumb(`User action: ${action}`, 'user', data);
  
  // Also capture as a message for analytics
  await captureMessage(`User performed action: ${action}`, 'info', {
    tags: {
      action_type: 'user_interaction',
      action_name: action
    },
    extra: data
  });
};

/**
 * Monitor SQL query execution
 * @param {string} query - SQL query
 * @param {string} agentId - Agent ID
 * @param {Object} result - Query result
 * @param {Error} error - Query error (if any)
 */
export const trackSQLQuery = async (query, agentId, result = null, error = null) => {
  const context = {
    tags: {
      component: 'sql_engine',
      agent_id: agentId,
      query_status: error ? 'failed' : 'success'
    },
    extra: {
      query_length: query?.length || 0,
      result_count: result?.length || 0,
      agent_id: agentId
    }
  };
  
  if (error) {
    await captureException(error, {
      ...context,
      tags: {
        ...context.tags,
        error_type: 'sql_error'
      },
      extra: {
        ...context.extra,
        sql_query: query?.substring(0, 500) // Limit query length for privacy
      }
    });
  } else {
    await addSentryBreadcrumb('SQL query executed successfully', 'sql', {
      agent_id: agentId,
      result_count: result?.length || 0
    });
  }
};

/**
 * Update a Sentry setting and refresh configuration
 * @param {string} settingName - Name of the setting (with sentry_ prefix)
 * @param {any} value - New value
 * @param {string} type - Type of setting
 * @returns {Promise<boolean>} Success status
 */
export const updateSentrySetting = async (settingName, value, type = 'string') => {
  try {
    const success = await settingsService.updateSetting(settingName, value, type);
    
    if (success) {
      // Clear cache to force refresh
      clearSentryCache();
      console.log(`✅ Sentry setting ${settingName} updated to:`, value);
    }
    
    return success;
  } catch (error) {
    console.error(`❌ Failed to update Sentry setting ${settingName}:`, error);
    throw error;
  }
};

/**
 * Get current Sentry configuration (for debugging/admin purposes)
 * @returns {Promise<Object>} Current Sentry configuration
 */
export const getSentryConfiguration = async () => {
  return await getSentryConfig();
};

// Export Sentry for direct use if needed
export { Sentry };

// Default export for convenience
export default {
  init: initSentry,
  setUser: setSentryUser,
  clearUser: clearSentryUser,
  addBreadcrumb: addSentryBreadcrumb,
  captureException,
  captureMessage,
  startTransaction,
  monitorApiCall,
  trackUserAction,
  trackSQLQuery,
  isSentryEnabled,
  clearCache: clearSentryCache,
  updateSetting: updateSentrySetting,
  getConfiguration: getSentryConfiguration,
  Sentry
}; 